import { useEffect, useState } from 'react'
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Loader2 } from 'lucide-react'
import { AuthProvider, useAuth } from './lib/AuthContext'
import * as api from './lib/api'
import { supabase } from './lib/supabaseClient'
import { AccountPage } from './pages/AccountPage'
import { AuthLoginPage } from './pages/AuthLoginPage'
import { CheckEmailPage } from './pages/CheckEmailPage'
import { ConfiguratorPage } from './pages/ConfiguratorPage'
import { InstallPage } from './pages/InstallPage'
import { OnboardPage } from './pages/OnboardPage'
import { PricingPage } from './pages/PricingPage'
import { UpdatePasswordPage } from './pages/UpdatePasswordPage'
import { Nav } from './components/Nav'
import { TermsPrivacyFooter } from './components/TermsPrivacyFooter'

export function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* A2F-619: authenticated users redirect to /configure so they
                don't re-do the acquisition flow. Anonymous visitors see the
                scrolling onboarding experience as before (A2F-546).
                A2F-766: the FRE now renders the v2 configurator unified with
                /configure — the side-by-side /pro-v2 route was retired. */}
            <Route path="/" element={<OnboardOrRedirect />} />
            {/* A2F-619: /configure shows an inline login form for anonymous
                users instead of redirecting to /. Authenticated users get
                the configurator with Nav + Simple/Advanced toggle. */}
            <Route path="/configure" element={<ConfigureRoute />} />
            <Route path="/install" element={<InstallPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/check-email" element={<CheckEmailPage />} />
            <Route path="/update-password" element={<UpdatePasswordPage />} />
            {/* A2F-695: dedicated /auth/login route the Pro upgrade card's
                "Sign in" CTA links to. Accepts ?redirect= so post-login
                the user lands back on whichever URL the taskpane was
                looking at, not the default /configure that ConfigureRoute's
                inline form lands on. */}
            <Route path="/auth/login" element={<AuthLoginPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  )
}

// Reset scroll to the top on every client-side route change. React Router's
// BrowserRouter doesn't restore scroll, so navigating from a scrolled-down
// page (e.g. the long FRE at `/`) to /pricing otherwise landed mid-page
// (A2F-938 review feedback).
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// A2F-619: redirect authenticated visitors away from the acquisition page.
// A2F-650: split the destination — users who already have a saved profile
// land on /configure to keep editing; users who confirmed but never landed
// on /install (the cross-device or stranded-account case) go to /install
// to finish setup. The FRE itself is for new, anonymous users only.
function OnboardOrRedirect() {
  const { session, loading } = useAuth()
  const [destination, setDestination] = useState<
    '/configure' | '/install' | null
  >(null)

  useEffect(() => {
    if (!session) {
      setDestination(null)
      return
    }
    let cancelled = false
    api
      .getProfile()
      .then(() => {
        if (!cancelled) setDestination('/configure')
      })
      .catch((err) => {
        if (cancelled) return
        const status = (err as { status?: number } | null)?.status
        // 404 = no saved profile yet; send to /install which handles the
        // pending-profile promote + first-save flow. Any other error
        // (network, 5xx) also routes to /install — it's the safer
        // fallback than punting back to the FRE for an authed user.
        setDestination(status === 404 ? '/install' : '/install')
      })
    return () => {
      cancelled = true
    }
  }, [session])

  if (loading) return null
  if (session) {
    if (!destination) return null
    return <Navigate to={destination} replace />
  }
  return <OnboardPage />
}

// A2F-619: /configure gate — login form for anonymous, configurator for auth
function ConfigureRoute() {
  const { session, loading, signInWithPassword } = useAuth()
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple')

  if (loading) return null
  if (!session) return <ConfigureLoginForm signIn={signInWithPassword} />

  return (
    <>
      <Nav />
      <ModeToggleBar mode={mode} onSwitch={setMode} />
      {mode === 'simple' ? (
        <ConfiguratorPage />
      ) : (
        <AdvancedPlaceholder onSwitch={() => setMode('simple')} />
      )}
    </>
  )
}

// ── Simple / Advanced mode toggle bar ────────────────────────────────────────

function ModeToggleBar({
  mode,
  onSwitch,
}: {
  mode: 'simple' | 'advanced'
  onSwitch: (m: 'simple' | 'advanced') => void
}) {
  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-5xl mx-auto px-6 py-2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => onSwitch('simple')}
          className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
            mode === 'simple'
              ? 'bg-slate-100 text-brand-charcoal'
              : 'text-slate-500 hover:text-brand-charcoal'
          }`}
        >
          Simple
        </button>
        <button
          type="button"
          onClick={() => onSwitch('advanced')}
          className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
            mode === 'advanced'
              ? 'bg-slate-100 text-brand-charcoal'
              : 'text-slate-500 hover:text-brand-charcoal'
          }`}
        >
          Advanced
        </button>
      </div>
    </div>
  )
}

// ── Advanced mode placeholder ─────────────────────────────────────────────────

function AdvancedPlaceholder({ onSwitch }: { onSwitch: () => void }) {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans">
      <div className="max-w-2xl mx-auto px-6 py-16 flex flex-col items-center text-center space-y-4">
        <div className="h-12 w-12 rounded-xl bg-brand-orange/10 grid place-items-center">
          <span className="text-xl">✦</span>
        </div>
        <h2 className="text-xl font-semibold">Advanced configurator coming soon</h2>
        <p className="text-sm text-slate-500 leading-relaxed max-w-md">
          Full control over rule authoring, live document preview, and custom
          validation logic. We&apos;re building it now.
        </p>
        <button
          type="button"
          onClick={onSwitch}
          className="text-sm font-medium text-brand-orange hover:underline"
        >
          Switch to Simple mode →
        </button>
      </div>
    </div>
  )
}

// ── Login form for /configure when unauthenticated ────────────────────────────

function ConfigureLoginForm({
  signIn,
}: {
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [resetting, setResetting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    const { error: signInError } = await signIn(email.trim(), password)
    setSubmitting(false)
    if (signInError) setError(signInError)
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError('Enter your email address above, then click Forgot password.')
      return
    }
    setResetting(true)
    setError(null)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo: `${window.location.origin}/update-password` },
      )
      if (resetError) throw resetError
      setResetSent(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not send reset email.')
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans grid place-items-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-brand-orange text-white text-sm font-bold">
            C
          </div>
          <span className="text-sm font-semibold tracking-wider text-brand-charcoal">
            CHRONA{' '}
            <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded text-brand-orange bg-brand-orange/10">
              PRO
            </span>
          </span>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
          <h1 className="text-base font-semibold text-center">Sign in to your account</h1>

          {resetSent ? (
            <div className="text-sm text-center text-slate-600 space-y-3">
              <p>Check your inbox — we sent a password reset link to <strong>{email}</strong>.</p>
              <button
                type="button"
                onClick={() => setResetSent(false)}
                className="text-brand-orange hover:underline text-xs"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="cfg-email" className="block text-xs font-medium text-slate-600">
                  Email
                </label>
                <input
                  id="cfg-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="cfg-password" className="block text-xs font-medium text-slate-600">
                  Password
                </label>
                <input
                  id="cfg-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 rounded-md bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-brand-orange text-white py-2.5 text-sm font-medium hover:bg-brand-orange/90 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                Sign in
              </button>

              <p className="text-xs text-center">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetting}
                  className="text-slate-500 hover:text-brand-orange disabled:opacity-50"
                >
                  {resetting ? 'Sending…' : 'Forgot password?'}
                </button>
              </p>
            </form>
          )}

          <p className="text-xs text-center text-slate-500">
            Don&apos;t have an account?{' '}
            <Link to="/" className="text-brand-orange font-medium hover:underline">
              Get started →
            </Link>
          </p>
        </div>
      </div>
      <div className="mt-8">
        <TermsPrivacyFooter />
      </div>
    </div>
  )
}
